import {
  Criterion,
  FieldChoice,
  ParticipantField,
  ParticipantFieldType,
} from '@app/api';
import { isCriterionDeclineChoice } from 'lib/criteria';

const transformOptionToChoice = (option: any): FieldChoice => {
  return {
    ref: `${option.value}`,
    label: option.label,
  };
};

const getValuesTextArrayFromOptions = (
  criterion: Criterion,
  options: Array<{ value: any; label: string }>,
): string[] => {
  const fieldEmpty = !criterion.allowList && !criterion.denyList.length;

  let values: string[] = [];
  if (!fieldEmpty) {
    values = options
      .filter((option) => {
        return !isCriterionDeclineChoice(
          criterion,
          transformOptionToChoice(option),
          true,
        );
      })
      .map((c) => c.label);
  }

  return values;
};

export const getSelectOptionsValuesTextArray = (
  criterion: Criterion,
  field: ParticipantField,
): string[] => {
  if (!field.choices) {
    return [];
  }
  return getValuesTextArrayFromOptions(criterion, field.choices);
};

export const getAllowListTextArray = (
  criterion: Criterion,
  _field: ParticipantField,
): string[] => {
  if (!criterion.allowList) {
    return [];
  }
  return criterion.allowList.map((value) => {
    if (typeof value === 'object') {
      if ('location' in value) {
        return value.name;
      } else if (value.min && value.max) {
        return value.min + ' - ' + value.max;
      } else if (value.max) {
        return '< ' + value.max;
      } else if (value.min) {
        return '> ' + value.min;
      } else {
        return '/';
      }
    }
    return String(value);
  });
};

/**
 * A locale criterion matches the regional variants of a language ("es" matches
 * "es-ES", "es-MX"...), so the selected values are listed rather than every choice
 * they match.
 */
export const getLocalesValuesTextArray = (
  criterion: Criterion,
  field: ParticipantField,
): string[] => {
  const choices = field.choices || [];
  if (!criterion.allowList) {
    return getValuesTextArrayFromOptions(criterion, choices);
  }
  return criterion.allowList.map((value) => {
    const choice = choices.find((c) => String(c.value) === String(value));
    return choice?.label || String(value);
  });
};

const criterionValuesTextsMap: Record<
  ParticipantFieldType,
  (criterion: Criterion, field: ParticipantField) => string[]
> = {
  [ParticipantFieldType.EMAIL]: getAllowListTextArray,
  [ParticipantFieldType.PHONE_NUMBER]: getAllowListTextArray,
  [ParticipantFieldType.SINGLE_CHOICE]: getSelectOptionsValuesTextArray,
  [ParticipantFieldType.MULTIPLE_CHOICE]: getSelectOptionsValuesTextArray,
  [ParticipantFieldType.TAGS]: getAllowListTextArray,
  [ParticipantFieldType.SHORT_TEXT]: getAllowListTextArray,
  [ParticipantFieldType.LONG_TEXT]: getAllowListTextArray,

  [ParticipantFieldType.DATE]: getAllowListTextArray,
  [ParticipantFieldType.NUMBER]: getAllowListTextArray,

  [ParticipantFieldType.GEOLOCATION]: getAllowListTextArray,
  [ParticipantFieldType.INCOME]: getSelectOptionsValuesTextArray,
  [ParticipantFieldType.TIMEZONE]: getAllowListTextArray,
  [ParticipantFieldType.LOCALES]: getLocalesValuesTextArray,

  [ParticipantFieldType.BOOLEAN]: getAllowListTextArray,
};

export const getCriterionValuesTextArray = (
  criterion: Criterion,
  field: ParticipantField,
): string[] => {
  const cb = criterionValuesTextsMap[field.type];
  return cb(criterion, field);
};
